// product-modal.js - Enhanced Product Modal with Dynamic Naming
class ProductModal {
    constructor(catalog) {
        this.catalog = catalog;
        this.currentProduct = null;
        this.selectedVariant = null;
        this.selectedAttributes = {};
        this.createModal();
        this.setupEventListeners();
    }

    // ... (keeping existing methods unchanged: createModal, setupEventListeners, open, close, isOpen)
    
    createModal() {
        const modalHTML = `
            <div id="product-modal" class="product-modal" style="display: none;" aria-hidden="true">
                <div class="modal-overlay" onclick="window.productModal.close()"></div>
                <div class="modal-content" role="dialog" aria-modal="true">
                    <button class="modal-close" onclick="window.productModal.close()" aria-label="Close modal">
                        <i class="ph ph-x"></i>
                    </button>
                    
                    <div class="modal-body">
                        <div class="modal-loading">
                            <div class="loader"></div>
                        </div>
                    </div>
                </div>
            </div>
        `;
        document.body.insertAdjacentHTML('beforeend', modalHTML);
    }

    setupEventListeners() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen()) {
                this.close();
            }
        });
    }

    async open(productId) {
        const product = this.catalog.products.find(p => p.$id === productId);
        if (!product) {
            console.error('Product not found:', productId);
            return;
        }

        this.currentProduct = product;
        this.selectedAttributes = {};
        this.selectedVariant = null;

        const modal = document.getElementById('product-modal');
        modal.style.display = 'flex';
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        await this.loadProductData();
        console.log('✅ Product modal opened for:', product.name);
    }

    close() {
        const modal = document.getElementById('product-modal');
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = 'auto';
        
        document.querySelector('#product-modal .modal-body').innerHTML = `
            <div class="modal-loading"><div class="loader"></div></div>`;

        this.currentProduct = null;
        this.selectedVariant = null;
        this.selectedAttributes = {};
    }

    isOpen() {
        const modal = document.getElementById('product-modal');
        return modal && modal.style.display !== 'none';
    }

    async loadProductData() {
        if (!this.currentProduct) return;

        const product = this.currentProduct;
        const category = this.catalog.categories.find(cat => cat.$id === product.category_id);
        
        let images = [this.catalog.getDefaultProductImage()];
        if (product.images) {
            try {
                const parsedImages = JSON.parse(product.images);
                if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                    images = parsedImages;
                }
            } catch (error) { console.error('Error parsing product images:', error); }
        }

        const modalBody = document.querySelector('#product-modal .modal-body');
        modalBody.innerHTML = `
            <div class="product-images">
                <div class="main-image">
                    <img id="modal-main-image" src="${this.catalog.escapeHtml(images[0])}" alt="${this.catalog.escapeHtml(product.name)}">
                </div>
                <div class="image-thumbnails" id="modal-thumbnails">
                    ${images.map((img, index) => `
                        <img src="${this.catalog.escapeHtml(img)}" 
                             alt="${this.catalog.escapeHtml(product.name)} - image ${index + 1}" 
                             class="thumbnail ${index === 0 ? 'active' : ''}"
                             onclick="window.productModal.switchImage(this.src, this)">
                    `).join('')}
                </div>
            </div>

            <div class="product-details">
                <div class="details-content-wrapper">
                    <div class="product-header">
                        <span class="product-category">${category ? this.catalog.escapeHtml(category.name) : 'Product'}</span>
                        <h2 class="product-title" id="modal-product-title">${this.catalog.escapeHtml(product.name)}</h2>
                    </div>

                    <div class="product-price" id="modal-price"></div>

                    <div class="product-variants" id="modal-variants" style="display: none;"></div>
                    
                    <div class="stock-status" id="modal-stock" style="display: none;">
                        <span class="stock-indicator"></span>
                        <span class="stock-text"></span>
                    </div>

                    <div class="product-description">${this.catalog.escapeHtml(product.description || product.short_description || '')}</div>
                </div>

                <div class="product-actions">
                    <button class="get-quote-btn" id="modal-quote-btn" onclick="window.productModal.requestQuote()">
                        <i class="ph ph-whatsapp-logo"></i>
                        <span id="modal-btn-text">Get Quote on WhatsApp</span>
                    </button>
                </div>
            </div>
        `;

        if (images.length <= 1) {
            document.getElementById('modal-thumbnails').style.display = 'none';
        }

        await this.renderAttributeSelectors();
        this.updatePrice();
        this.updateStock();
        this.updateActionButton();
    }

    switchImage(imageUrl, thumbElement) {
        document.getElementById('modal-main-image').src = imageUrl;
        document.querySelectorAll('.thumbnail').forEach(thumb => thumb.classList.remove('active'));
        thumbElement.classList.add('active');
    }

    async renderAttributeSelectors() {
        const product = this.currentProduct;
        const variantsContainer = document.getElementById('modal-variants');
        if (!product.selected_attributes || !product.variants_data) {
            variantsContainer.style.display = 'none';
            return;
        }

        try {
            const attributeIds = JSON.parse(product.selected_attributes);
            const variantsData = JSON.parse(product.variants_data);
            if (attributeIds.length === 0 || variantsData.length === 0) {
                variantsContainer.style.display = 'none';
                return;
            }

            const attributes = this.getAttributesFromVariants(variantsData);
            if (attributes.length === 0) return;

            const selectorsHTML = attributes.map(attr => `
                <div class="variant-group">
                    <h4 class="variant-group-title">${this.catalog.escapeHtml(attr.name)}:</h4>
                    <div class="variant-options" data-attribute-id="${this.catalog.escapeHtml(attr.id)}">
                        ${attr.values.map(val => `
                            <button class="variant-option" data-value="${this.catalog.escapeHtml(val)}">
                                ${this.catalog.escapeHtml(val)}
                            </button>
                        `).join('')}
                    </div>
                </div>
            `).join('');

            variantsContainer.innerHTML = selectorsHTML;
            variantsContainer.style.display = 'block';

            document.querySelectorAll('.variant-option').forEach(button => {
                button.addEventListener('click', (e) => this.onAttributeSelect(e));
            });

        } catch (error) {
            console.error('Error loading product variants:', error);
            variantsContainer.style.display = 'none';
        }
    }
    
    getAttributesFromVariants(variantsData) {
        const attributesMap = {};
        variantsData.forEach(variant => {
            Object.values(variant.attributes).forEach(attr => {
                if (!attributesMap[attr.attributeId]) {
                    attributesMap[attr.attributeId] = {
                        id: attr.attributeId,
                        name: attr.attributeName,
                        values: new Set()
                    };
                }
                attributesMap[attr.attributeId].values.add(attr.value);
            });
        });
        return Object.values(attributesMap).map(attr => ({ ...attr, values: Array.from(attr.values) }));
    }

    onAttributeSelect(event) {
        const selectedButton = event.currentTarget;
        const group = selectedButton.parentElement;
        const wasActive = selectedButton.classList.contains('active');

        group.querySelectorAll('.variant-option').forEach(btn => btn.classList.remove('active'));

        if (!wasActive) {
            selectedButton.classList.add('active');
        }

        this.updateStateFromSelection();
    }

    updateStateFromSelection() {
        this.selectedAttributes = {};
        document.querySelectorAll('.variant-option.active').forEach(button => {
            const attributeId = button.parentElement.dataset.attributeId;
            const value = button.dataset.value;
            this.selectedAttributes[attributeId] = value;
        });

        this.findMatchingVariant();
        this.updatePrice();
        this.updateStock();
        this.updateActionButton();
        this.updateProductTitle(); // NEW: Update title when variants change
    }
    
    findMatchingVariant() {
        if (!this.currentProduct.variants_data) {
            this.selectedVariant = null;
            return;
        }
        try {
            const variantsData = JSON.parse(this.currentProduct.variants_data);
            const selectedIds = Object.keys(this.selectedAttributes);

            this.selectedVariant = variantsData.find(variant => {
                const variantAttrIds = Object.keys(variant.attributes);
                if (selectedIds.length !== variantAttrIds.length) return false;
                return selectedIds.every(attrId => 
                    variant.attributes[attrId] && 
                    variant.attributes[attrId].value === this.selectedAttributes[attrId]
                );
            });
        } catch (error) {
            console.error('Error finding matching variant:', error);
            this.selectedVariant = null;
        }
    }

    // NEW: Dynamic Product Title Update
    updateProductTitle() {
        const titleElement = document.getElementById('modal-product-title');
        if (!titleElement || !this.currentProduct) return;

        const baseName = this.currentProduct.name;
        const variantSuffix = this.generateVariantSuffix();
        
        const dynamicTitle = variantSuffix ? `${baseName} (${variantSuffix})` : baseName;
        titleElement.textContent = dynamicTitle;
    }

    // NEW: Generate variant suffix for product name
    generateVariantSuffix() {
        if (Object.keys(this.selectedAttributes).length === 0) return '';

        const variantParts = [];
        
        // Get attribute details from variants data
        try {
            const variantsData = JSON.parse(this.currentProduct.variants_data || '[]');
            const attributeOrder = ['Size', 'Finish', 'Material', 'Color']; // Preferred order
            
            // Create ordered list of selected attributes
            const selectedByName = {};
            Object.entries(this.selectedAttributes).forEach(([attrId, value]) => {
                const variant = variantsData.find(v => v.attributes[attrId]);
                if (variant) {
                    const attrName = variant.attributes[attrId].attributeName;
                    selectedByName[attrName] = value;
                }
            });

            // Add attributes in preferred order
            attributeOrder.forEach(attrName => {
                if (selectedByName[attrName]) {
                    variantParts.push(selectedByName[attrName]);
                }
            });

            // Add any remaining attributes not in the preferred order
            Object.entries(selectedByName).forEach(([attrName, value]) => {
                if (!attributeOrder.includes(attrName)) {
                    variantParts.push(value);
                }
            });

        } catch (error) {
            console.error('Error generating variant suffix:', error);
            return '';
        }

        return variantParts.join(' - ');
    }

    // NEW: Get current dynamic product name (for WhatsApp)
    getCurrentProductName() {
        const baseName = this.currentProduct.name;
        const variantSuffix = this.generateVariantSuffix();
        return variantSuffix ? `${baseName} (${variantSuffix})` : baseName;
    }

    parsePrice(price) {
        if (price === null || price === undefined || price === '') {
            return 0;
        }
        const parsed = parseFloat(price);
        return isNaN(parsed) ? 0 : parsed;
    }
    
    getCurrentPricingState() {
        const basePrice = this.parsePrice(this.currentProduct.base_price);
        
        if (this.selectedVariant) {
            const variantPrice = this.parsePrice(this.selectedVariant.price);
            return {
                price: variantPrice,
                hasPrice: variantPrice > 0,
                isVariant: true
            };
        } else if (this.hasVariants()) {
            const variantPrices = this.getAllVariantPrices();
            if (variantPrices.length > 0) {
                const minPrice = Math.min(...variantPrices);
                const maxPrice = Math.max(...variantPrices);
                return {
                    price: minPrice,
                    maxPrice: maxPrice,
                    hasPrice: true,
                    isVariant: false,
                    isRange: minPrice !== maxPrice
                };
            } else if (basePrice > 0) {
                return {
                    price: basePrice,
                    hasPrice: true,
                    isVariant: false
                };
            } else {
                return {
                    price: 0,
                    hasPrice: false,
                    isVariant: false
                };
            }
        } else {
            return {
                price: basePrice,
                hasPrice: basePrice > 0,
                isVariant: false
            };
        }
    }
    
    hasVariants() {
        if (!this.currentProduct.selected_attributes || !this.currentProduct.variants_data) {
            return false;
        }
        try {
            const selectedAttributes = JSON.parse(this.currentProduct.selected_attributes);
            const variantsData = JSON.parse(this.currentProduct.variants_data);
            return selectedAttributes.length > 0 && variantsData.length > 0;
        } catch (error) {
            return false;
        }
    }
    
    getAllVariantPrices() {
        if (!this.currentProduct.variants_data) return [];
        
        try {
            const variantsData = JSON.parse(this.currentProduct.variants_data);
            return variantsData
                .map(variant => this.parsePrice(variant.price))
                .filter(price => price > 0);
        } catch (error) {
            return [];
        }
    }

    updatePrice() {
        const priceElement = document.getElementById('modal-price');
        if (!this.currentProduct || !priceElement) return;

        const pricingState = this.getCurrentPricingState();
        
        if (!pricingState.hasPrice) {
            priceElement.innerHTML = `<span class="price-request">Request Price</span>`;
            priceElement.className = 'product-price request-price';
        } else if (pricingState.isVariant) {
            priceElement.innerHTML = `<span class="price-amount">₹${pricingState.price.toLocaleString('en-IN')}</span>`;
            priceElement.className = 'product-price variant-price';
        } else if (pricingState.isRange) {
            priceElement.innerHTML = `<span class="price-range">₹${pricingState.price.toLocaleString('en-IN')} - ₹${pricingState.maxPrice.toLocaleString('en-IN')}</span>`;
            priceElement.className = 'product-price range-price';
        } else {
            priceElement.innerHTML = `<span class="price-amount">₹${pricingState.price.toLocaleString('en-IN')}</span>`;
            priceElement.className = 'product-price fixed-price';
        }
    }

    updateStock() {
        const stockElement = document.getElementById('modal-stock');
        if (this.selectedVariant && this.selectedVariant.stock !== null && this.selectedVariant.stock !== undefined) {
            const stock = parseInt(this.selectedVariant.stock, 10);
            if (!isNaN(stock)) {
                const indicator = stockElement.querySelector('.stock-indicator');
                const text = stockElement.querySelector('.stock-text');
                
                indicator.className = `stock-indicator ${stock > 0 ? 'in-stock' : 'out-of-stock'}`;
                text.textContent = stock > 0 ? 'In Stock' : 'Out of Stock';
                stockElement.style.display = 'flex';
                return;
            }
        }
        stockElement.style.display = 'none';
    }
    
    updateActionButton() {
        const button = document.getElementById('modal-quote-btn');
        const buttonText = document.getElementById('modal-btn-text');
        
        if (!button || !buttonText) return;
        
        const pricingState = this.getCurrentPricingState();
        
        if (!pricingState.hasPrice) {
            button.className = 'get-quote-btn request-quote';
            buttonText.textContent = 'Request Quote on WhatsApp';
            button.querySelector('i').className = 'ph ph-whatsapp-logo';
        } else {
            button.className = 'get-quote-btn has-price';
            if (pricingState.isVariant) {
                buttonText.textContent = 'Get Quote for This Option';
            } else {
                buttonText.textContent = 'Get Quote on WhatsApp';
            }
            button.querySelector('i').className = 'ph ph-whatsapp-logo';
        }
    }

    requestQuote() {
        if (!this.currentProduct) return;
        const phoneNumber = '919558229458';
        
        const pricingState = this.getCurrentPricingState();
        const dynamicProductName = this.getCurrentProductName(); // NEW: Use dynamic name
        
        let message = `Hi! I'm interested in a quote for the following product:\n\n`;
        message += `*Product:* ${dynamicProductName}\n`; // NEW: Dynamic name in WhatsApp
        
        if (pricingState.hasPrice) {
            if (pricingState.isVariant) {
                message += `*Listed Price:* ₹${pricingState.price.toLocaleString('en-IN')}\n`;
            } else if (pricingState.isRange) {
                message += `*Price Range:* ₹${pricingState.price.toLocaleString('en-IN')} - ₹${pricingState.maxPrice.toLocaleString('en-IN')}\n`;
            } else {
                message += `*Listed Price:* ₹${pricingState.price.toLocaleString('en-IN')}\n`;
            }
        } else {
            message += `*Pricing:* Please provide quote\n`;
        }
        
        message += `\n*Quantity Needed:* [Please specify]\n`;
        message += `*Additional Requirements:* [Any specific requirements or questions]\n`;
        message += `\nFrom your website: ${window.location.host}`;
        message += `\n\nPlease provide detailed pricing and availability information. Thank you!`;
        
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const initModal = () => {
        if (window.productCatalog) {
            window.productModal = new ProductModal(window.productCatalog);
            console.log('✅ Product modal with dynamic naming initialized');
        } else {
            setTimeout(initModal, 100);
        }
    };
    initModal();
});